import { addHours, format, startOfDay } from "date-fns";

import { address as addressModel } from "@repo/models";

import * as constants from "./constants";
import { MINUTES_IN_HOUR } from "./constants";
import { hasValue } from "./general";

/**Formats name into a string */
function name(firstName: string, lastName: string, normal?: boolean) {
	if (!firstName || !lastName) return firstName || lastName;
	if (!normal) return lastName + ", " + firstName;
	return firstName + " " + lastName;
}

/** Formats address lines into string */
function address(addressLine1: string, addressLine2: string) {
	if (addressLine1 && addressLine2) return addressLine1 + " " + addressLine2;
	if (addressLine1) return addressLine1;
	return addressLine2;
}

/** Formats address lines into url encoded address object */
function addressForUrl(address: addressModel) {
	return {
		...address,
		address2: address.address2 ?? "",
	} as addressModel;
}

/** Format city and state info into string */
function cityState(city: string, state: string, zip: string | number) {
	let str = "";
	if (city && state) str = city + ", " + state;
	else if (city) str = city;
	else if (state) str = state;

	if (zip) {
		if (str) str += " ";
		str += zip;
	}
	return str;
}

/** Format address info into single string */
function fullAddress(addressLine1: string, addressLine2: string, city: string, state: string, zip: string | number) {
	const addressLine = address(addressLine1, addressLine2);
	const cityLine = cityState(city, state, zip);
	if (addressLine && cityLine) return addressLine + " " + cityLine;
	if (addressLine) return addressLine;
	return cityLine;
}

/** Format phone number into single string
 * NOTE: currently doesn't handle extensions
 */
function phone(number: string | number) {
	if (!number) return number;
	let onlyDigits = number.toString().replace(/\D/g, "");
	if (onlyDigits.length < 10) return onlyDigits;
	let formatted = "";
	if (onlyDigits.length > 10) {
		const extraDigits = onlyDigits.length - 10;
		formatted += "+";
		formatted += onlyDigits.slice(0, extraDigits);
		formatted += " ";
		onlyDigits = onlyDigits.slice(extraDigits);
	}
	formatted += "(" + onlyDigits.slice(0, 3) + ") ";
	formatted += onlyDigits.slice(3, 6);
	formatted += "-" + onlyDigits.slice(6);
	return formatted;
}

/** Format phone number into single string
 * NOTE: currently doesn't handle extensions
 */
function phoneOnlyNumeric(number: string) {
	if (!number) return number;
	return number.replace(/\D/g, "");
}

const fullTimeFromSeconds = (totalSeconds: number) => timeFromSeconds(totalSeconds, true);

/** Formats total seconds into hours and minutes and seconds */
function timeFromSeconds(totalSeconds: number, full?: boolean) {
	if (!hasValue(totalSeconds)) return null;
	const totalMinutes = Math.floor(totalSeconds / constants.SECONDS_IN_MINUTE);
	const hours = Math.floor(totalMinutes / constants.MINUTES_IN_HOUR);
	const minutes = totalMinutes % constants.MINUTES_IN_HOUR;
	const seconds = totalSeconds % constants.SECONDS_IN_MINUTE;

	const displayingHours = full || hours;

	const dispSeconds = (seconds < 10 ? "0" : "") + seconds;
	const dispMinutes = (displayingHours && minutes < 10 ? "0" : "") + minutes;
	let time = dispMinutes + ":" + dispSeconds;
	if (displayingHours) time = hours + ":" + time;
	return time;
}

/** Formats date into date-only string */
function dateOf(date: Date | string, fullYear = true) {
	if (!date) return null;
	if (typeof date === "string") date = new Date(date);
	const options = fullYear
		? undefined
		: ({
				month: "numeric",
				day: "numeric",
				year: "2-digit",
			} as Intl.DateTimeFormatOptions);
	return date.toLocaleDateString([], options);
}

/** Formats date into time-only string */
function timeOf(date: Date | string, displaySeconds = true) {
	if (!date) return null;
	if (typeof date === "string") date = new Date(date);
	const options = displaySeconds ? undefined : ({ hour: "2-digit", minute: "2-digit" } as Intl.DateTimeFormatOptions);
	return date.toLocaleTimeString([], options);
}

/** Formats date into date/time string */
function dateTimeOf(date: Date | string, displaySeconds = true, fullYear = true) {
	if (!date) return null;
	if (typeof date === "string") date = new Date(date);
	return dateOf(date, fullYear) + " " + timeOf(date, displaySeconds);
}

const shortDateOf = (date: Date | string) => dateOf(date, false);

/** Date Time, without seconds */
const shortDateTimeOf = (date: Date | string) => dateTimeOf(date, false, false);

function holdInUtc(date: Date | string) {
	if (!date) return null;
	if (typeof date === "string") date = new Date(date);
	const tzOffset = date.getTimezoneOffset();
	if (tzOffset) date.setHours(date.getHours() + tzOffset / MINUTES_IN_HOUR);
	return date;
}

function holdPropsInUtc<T extends Partial<Record<K, Date>>, K extends keyof T>(obj: T, ...props: K[]) {
	for (const prop of props) {
		const newValue = holdInUtc(obj[prop]);
		Object.assign(obj, { [prop]: newValue });
	}
}

function originalDateOf(date: Date | string) {
	return dateOf(holdInUtc(date));
}
/**
 * Returns date, or null if date is empty or <1800
 */
function dummyNull(date: Date | string) {
	if (!date) return null;
	date = new Date(date);
	if (date.getFullYear() <= 1800) return null;
	return date;
}

function splitWordsOnUpperCase(str: string | undefined) {
	return str?.replace(/(?<=[A-Z])(?=[A-Z][a-z])|(?<=[^A-Z])(?=[A-Z])|(?<=[A-Za-z])(?=[^A-Za-z])/gm, " ");
}

function snakeToPascal(snakeCase: string): string {
	const words = snakeCase.split("_");
	return words
		.map((word, index) => {
			// Capitalize the first letter of each word
			return index === 0
				? word.charAt(0).toUpperCase() + word.slice(1)
				: word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
		})
		.join("");
}

export function camelToPascal(str: string) {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export function camelToStartCase(str: string) {
	const pascal = camelToPascal(str);
	return splitWordsOnUpperCase(pascal);
}

function formatPercent(value: number) {
	return (value * 100).toFixed(0) + "%";
}

function formatBool(value: boolean) {
	return value ? "Yes" : "No";
}

function currency(amount: number, includeDecimal = true) {
	amount = amount ?? 0;
	const formatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: includeDecimal ? 2 : 0,
		maximumFractionDigits: includeDecimal ? 2 : 0,
	});
	return formatter.format(amount);
}

export function intToTime(hour: number) {
	const date = addHours(startOfDay(new Date()), hour);
	return format(date, "haaa").toLowerCase(); // 'haaa' formats the hour, followed by am/pm
}

export {
	name,
	address,
	addressForUrl,
	cityState,
	fullAddress,
	phone,
	phoneOnlyNumeric,
	timeFromSeconds,
	fullTimeFromSeconds,
	dateOf,
	timeOf,
	dateTimeOf,
	shortDateTimeOf,
	shortDateOf,
	holdInUtc,
	holdPropsInUtc,
	originalDateOf,
	dummyNull,
	splitWordsOnUpperCase,
	snakeToPascal,
	formatPercent,
	formatBool,
	currency,
};
